/** The App Store locale catalog (App Store Connect localization codes). */
export interface AppStoreLocale {
  code: string;
  name: string;
  flag: string;
}

export const APP_STORE_LOCALES: AppStoreLocale[] = [
  { code: 'ar-SA', name: 'Arabic', flag: '🇸🇦' },
  { code: 'bn-BD', name: 'Bangla', flag: '🇧🇩' },
  { code: 'ca', name: 'Catalan', flag: '🇪🇸' },
  { code: 'zh-Hans', name: 'Chinese (Simplified)', flag: '🇨🇳' },
  { code: 'zh-Hant', name: 'Chinese (Traditional)', flag: '🇹🇼' },
  { code: 'hr', name: 'Croatian', flag: '🇭🇷' },
  { code: 'cs', name: 'Czech', flag: '🇨🇿' },
  { code: 'da', name: 'Danish', flag: '🇩🇰' },
  { code: 'nl-NL', name: 'Dutch', flag: '🇳🇱' },
  { code: 'en-AU', name: 'English (Australia)', flag: '🇦🇺' },
  { code: 'en-CA', name: 'English (Canada)', flag: '🇨🇦' },
  { code: 'en-GB', name: 'English (U.K.)', flag: '🇬🇧' },
  { code: 'en-US', name: 'English (U.S.)', flag: '🇺🇸' },
  { code: 'fi', name: 'Finnish', flag: '🇫🇮' },
  { code: 'fr-FR', name: 'French', flag: '🇫🇷' },
  { code: 'fr-CA', name: 'French (Canada)', flag: '🇨🇦' },
  { code: 'de-DE', name: 'German', flag: '🇩🇪' },
  { code: 'el', name: 'Greek', flag: '🇬🇷' },
  { code: 'gu-IN', name: 'Gujarati', flag: '🇮🇳' },
  { code: 'he', name: 'Hebrew', flag: '🇮🇱' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
  { code: 'hu', name: 'Hungarian', flag: '🇭🇺' },
  { code: 'id', name: 'Indonesian', flag: '🇮🇩' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'kn-IN', name: 'Kannada', flag: '🇮🇳' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
  { code: 'ms', name: 'Malay', flag: '🇲🇾' },
  { code: 'ml-IN', name: 'Malayalam', flag: '🇮🇳' },
  { code: 'mr-IN', name: 'Marathi', flag: '🇮🇳' },
  { code: 'no', name: 'Norwegian', flag: '🇳🇴' },
  { code: 'or-IN', name: 'Odia', flag: '🇮🇳' },
  { code: 'pl', name: 'Polish', flag: '🇵🇱' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)', flag: '🇧🇷' },
  { code: 'pt-PT', name: 'Portuguese (Portugal)', flag: '🇵🇹' },
  { code: 'pa-IN', name: 'Punjabi', flag: '🇮🇳' },
  { code: 'ro', name: 'Romanian', flag: '🇷🇴' },
  { code: 'ru', name: 'Russian', flag: '🇷🇺' },
  { code: 'sk', name: 'Slovak', flag: '🇸🇰' },
  { code: 'sl-SI', name: 'Slovenian', flag: '🇸🇮' },
  { code: 'es-MX', name: 'Spanish (Mexico)', flag: '🇲🇽' },
  { code: 'es-ES', name: 'Spanish (Spain)', flag: '🇪🇸' },
  { code: 'sv', name: 'Swedish', flag: '🇸🇪' },
  { code: 'ta-IN', name: 'Tamil', flag: '🇮🇳' },
  { code: 'te-IN', name: 'Telugu', flag: '🇮🇳' },
  { code: 'th', name: 'Thai', flag: '🇹🇭' },
  { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
  { code: 'uk', name: 'Ukrainian', flag: '🇺🇦' },
  { code: 'ur-PK', name: 'Urdu', flag: '🇵🇰' },
  { code: 'vi', name: 'Vietnamese', flag: '🇻🇳' },
];

/**
 * Apple's public App Store Connect API rejects these languages only when they
 * are addressed by their *base* code (e.g. "'bn' is not a valid locale"). Apple
 * keys the March 2026 additions by their *regional* code — bn-BD, gu-IN, kn-IN,
 * ml-IN, mr-IN, or-IN, pa-IN, sl-SI, ta-IN, te-IN, ur-PK — which the catalog
 * above now uses. With the correct codes the API accepts all 50 locales for
 * reading *and* creation, and deep sync no longer drops them (isKnownLocale),
 * so this list is empty.
 *
 * Do NOT re-add the base codes here to "hide" these languages: that only masks
 * the symptom in add-language while sync still discards the regional
 * localizations, leaving them invisible in the dashboard. Keep the constant
 * (empty) so a genuine future regression can be listed without re-threading a
 * filter through locale creation.
 */
export const APP_STORE_CONNECT_API_PENDING_LOCALE_CODES: readonly string[] = [];

const apiPendingCodes = new Set<string>(APP_STORE_CONNECT_API_PENDING_LOCALE_CODES);

/** Locales that Apple's public API can currently create programmatically. */
export const APP_STORE_CONNECT_API_LOCALES = APP_STORE_LOCALES.filter(
  (locale) => !apiPendingCodes.has(locale.code),
);

const byCode = new Map(APP_STORE_LOCALES.map((l) => [l.code, l]));

export function localeInfo(code: string): AppStoreLocale {
  return byCode.get(code) ?? { code, name: code, flag: '🌐' };
}

export function localeName(code: string): string {
  return localeInfo(code).name;
}

export function isKnownLocale(code: string): boolean {
  return byCode.has(code);
}

export function isAppStoreConnectApiLocale(code: string): boolean {
  return byCode.has(code) && !apiPendingCodes.has(code);
}

/** Sort locale codes alphabetically by display name, with the given primary first. */
export function sortLocales(codes: string[], primary?: string): string[] {
  return [...codes].sort((a, b) => {
    if (a === primary) return -1;
    if (b === primary) return 1;
    return localeName(a).localeCompare(localeName(b));
  });
}
